import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PublicService {
  private url = environment.URL;
  private api_url  = environment.API_URL;

  constructor(private http: HttpClient) {}

  // ── AUTH (/auth) ─────────────────────────────────────────────────────────────

  /** POST /auth/register/v1 */
  register(data: any): Observable<any> {
    return this.http.post(`${this.url}auth/register/v1`, data);
  }

  /** POST /auth/login */
  login(data: any): Observable<any> {
    return this.http.post(`${this.url}auth/login`, data);
  }

  activeSession(data: any): Observable<any> {
    return this.http.post(`${this.url}auth/active-sessions`, data);
  }

  /** PATCH /auth/reset-password — sends forgot-password OTP */
  resetPassword(data: any): Observable<any> {
    return this.http.patch(`${this.url}auth/reset-password`, data);
  }

  /** PATCH /auth/update-password */
  updatePassword(data: any): Observable<any> {
    return this.http.patch(`${this.url}auth/update-password`, data);
  }

  /** POST /auth/verify-otp */
  verifyOtp(data: any): Observable<any> {
    return this.http.post(`${this.url}auth/verify-otp`, data);
  }

  /** POST /auth/verify-forget-password-otp */
  verifyForgetPasswordOtp(data: any): Observable<any> {
    return this.http.post(`${this.url}auth/verify-forget-password-otp`, data);
  }

  /** POST /auth/resend-otp */
  resendOtp(data: any): Observable<any> {
    return this.http.post(`${this.url}auth/resend-otp`, data);
  }

  /** POST /api/auth/social-login */
  socialLogin(data: any): Observable<any> {
    return this.http.post(`${this.api_url}auth/social-login`, data);
  }

  // ── USER (/user) ─────────────────────────────────────────────────────────────

  /** POST /user/check-valid — check email / phone availability */
  checkValid(data: any): Observable<any> {
    return this.http.post(`${this.url}user/check-valid`, data);
  }

  /** POST /api/user/accept-terms — authenticated, requires token */
  acceptTerms(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.api_url}user/accept-terms`, data, { headers });
  }

  // ── BANNER (/banner) ─────────────────────────────────────────────────────────

  /** GET /banner — fetch all active banners */
  getBanners(params?: any): Observable<any> {
    return this.http.get(`${this.url}banner`, { params });
  }

  // ── AUCTION (/auction) ───────────────────────────────────────────────────────

  /** POST /auction/active-auctions */
  getActiveAuctions(data: any): Observable<any> {
    return this.http.post(`${this.url}auction/active-auctions`, data);
  }

  /** POST /auction/get-auction */
  getAuctionDetail(data: any): Observable<any> {
    return this.http.post(`${this.url}auction/get-auction`, data);
  }

  /** POST /auction/active-auctions — with X-Special-Param header (auction join views) */
  auctionListWithDate(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.url}auction/active-auctions`, data, { headers });
  }

  /** GET /auction/auction-details */
  getAuctionDetails(params?: any): Observable<any> {
    return this.http.get(`${this.url}auction/auction-details`, { params });
  }

  /** GET /auction/auction-calendar */
  getAuctionCalendar(params?: any): Observable<any> {
    return this.http.get(`${this.url}auction/auction-calendar`, { params });
  }

  // ── MASTER (/master) ─────────────────────────────────────────────────────────

  /** POST /master/make-list — pass { make: 'TOYOTA' } to filter, omit for the full list */
  getMakeList(data: any = {}): Observable<any> {
    return this.http.post(`${this.url}master/make-list`, data);
  }

  /** POST /master/body-list */
  getBodyList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/body-list`, data);
  }

  /** POST /master/model-list */
  getModelList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/model-list`, data);
  }

  /** POST /master/branch-list */
  getBranchList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/branch-list`, data);
  }

  /** POST /master/buyer-limit-list */
  getBuyerLimitList(data?: any): Observable<any> {
    return this.http.post(`${this.url}master/buyer-limit-list`, data ?? {});
  }

  /** POST /master/seller-plan-list */
  getSellerPlanList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/seller-plan-list`, data);
  }

  /** POST /master/transmission-list */
  getTransmissionList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/transmission-list`, data);
  }

  /** POST /master/engine-list */
  getEngineList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/engine-list`, data);
  }

  /** POST /master/drivetype-list */
  getDrivetypeList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/drivetype-list`, data);
  }

  /** POST /master/fueltype-list */
  getFueltypeList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/fueltype-list`, data);
  }

  /** POST /master/interior-color-list */
  getInteriorColorList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/interior-color-list`, data);
  }

  /** POST /master/exterior-color-list */
  getExteriorColorList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/exterior-color-list`, data);
  }

  /** POST /master/series-list */
  getSeriesList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/series-list`, data);
  }

   /** POST /master/warehouse-list */
  getWarehouseList(data: any): Observable<any> {
    return this.http.post(`${this.url}master/warehouselist`, data);
  }


  // ── COMMON (/common) ─────────────────────────────────────────────────────────

  /** GET /common/get-current-db-time */
  getCurrentDbTime(): Observable<any> {
    return this.http.get(`${this.url}common/get-current-db-time`);
  }

  /** GET /common/generate-captcha */
  generateCaptcha(): Observable<any> {
    return this.http.get(`${this.url}common/generate-captcha`);
  }

  /** POST /common/create-ticket */
  createTicket(data: any): Observable<any> {
    return this.http.post(`${this.url}common/create-ticket`, data);
  }

  /** POST /common/get-all-job-vaccancies */
  getAllJobVaccancies(data: any): Observable<any> {
    return this.http.post(`${this.url}common/get-all-job-vaccancies`, data);
  }

  /** GET /common/job/:id */
  getJobById(id: string): Observable<any> {
    return this.http.get(`${this.url}common/job/${id}`);
  }

  /** POST /common/apply-for-job */
  applyForJob(data: any): Observable<any> {
    return this.http.post(`${this.url}common/apply-for-job`, data);
  }

  /** POST /common/send-otp */
  sendOtp(data: any): Observable<any> {
    return this.http.post(`${this.url}common/send-otp`, data);
  }

  // ── VEHICLE / INVENTORY (/vehicle) ───────────────────────────────────────────

  /** POST /vehicle/get-auction-vehicles */
  getAuctionVehicles(data: any): Observable<any> {
    return this.http.post(`${this.url}vehicle/get-auction-vehicles`, data);
  }

  /** GET /vehicle/inventory-details */
  getInventoryDetails(params?: any): Observable<any> {
    return this.http.get(`${this.url}vehicle/inventory-details`, { params });
  }

  /** POST /vehicle/inventory-by-auction-display */
  inventoryByAuctionDisplay(data: any): Observable<any> {
    return this.http.post(`${this.url}vehicle/inventory-by-auction-display`, data);
  }

  // ── BID (/bid) ───────────────────────────────────────────────────────────────

  /** POST /bid/get-pre-bids — optional auth (works without token too) */
  getPreBids(data: any): Observable<any> {
    return this.http.post(`${this.url}bid/get-pre-bids`, data);
  }

  // ── GETDBTIME (/getdbtime) ───────────────────────────────────────────────────

  /** GET /getdbtime/getCurrentTime — legacy db time endpoint */
  getDbTime(): Observable<any> {
    return this.http.get(`${this.url}getdbtime/getCurrentTime`);
  }

  // ── Legacy endpoints (still referenced by older components) ─────────────────

  /** POST user/register — legacy registration */
  registerUser(data: any): Observable<any> {
    return this.http.post(`${this.url}user/register`, data);
  }

  /** POST user/register/lead-user — lead user registration */
  registerUserLead(data: any): Observable<any> {
    return this.http.post(`${this.url}user/register/lead-user`, data);
  }

  /** POST user/update-profile-otp */
  updateProfileOtp(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Captcha-Token': 'CustomHeaderValue' });
    return this.http.post(`${this.url}user/update-profile-otp`, data, { headers });
  }

  /** POST termscondition/accept-terms-condition — legacy terms acceptance */
  acceptTermsConditionLegacy(data: any): Observable<any> {
    return this.http.post(`${this.url}termscondition/accept-terms-condition`, data);
  }

  /** POST termscondition/get-terms-condition — legacy get terms */
  getTermsCondition(data: any): Observable<any> {
    return this.http.post(`${this.url}termscondition/get-terms-condition`, data);
  }

  // ── Careers ──────────────────────────────────────────────────────────────────

  /** POST careers/jobs */
  getAllJobs(data: any): Observable<any> {
    return this.http.post(`${this.url}common/get-all-job-vaccancies`, data);
  }

  /** GET careers/job/:id */
  getCareerJobById(id: string): Observable<any> {
    return this.http.get(`${this.url}common/job/${id}`);
  }

  /** POST common/apply */
  applyForCareer(data: any): Observable<any> {
    return this.http.post(`${this.url}common/apply`, data);
  }
}
